import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    message: "",
    accepteContact: false,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = "Le nom est requis";
    if (!formData.prenom.trim()) newErrors.prenom = "Le prénom est requis";
    if (!formData.telephone.trim()) newErrors.telephone = "Le téléphone est requis";
    else if (!/^[0-9]{10}$/.test(formData.telephone.replace(/\s/g, ""))) {
      newErrors.telephone = "Numéro invalide";
    }
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }
    if (!formData.accepteContact) {
      newErrors.accepteContact = "Veuillez accepter d'être recontacté";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone,
        email: formData.email,
        accepte_contact: formData.accepteContact,
        notes: formData.message || "Contact depuis la page contact",
      });
      setSubmitted(true);
      toast.success("Message envoyé avec succès !");
    } catch (error) {
      console.error("Contact error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-harbor py-20" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl text-white">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 animate-fade-in-up">
              Contactez-nous
            </h1>
            <p className="text-lg text-white/80 animate-fade-in-up animation-delay-200">
              Une question sur votre projet immobilier ? Notre équipe est à votre
              disposition pour vous accompagner.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-spacing bg-paper" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-harbor mb-6">
                  Inter'med Immobilier
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Spécialiste de l'investissement locatif et résidentiel dans l'immobilier
                  neuf et ancien à Nantes et en Loire-Atlantique.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="p-3 bg-harbor/10 rounded-md text-harbor">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-harbor">Adresse</h3>
                    <p className="text-muted-foreground">
                      25 Rue de l'Indre<br />
                      44000 Nantes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <div className="p-3 bg-harbor/10 rounded-md text-harbor">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-harbor">Téléphone</h3>
                    <a href="tel:0749375868" className="text-muted-foreground hover:text-harbor transition-colors">
                      07 49 37 58 68
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="p-3 bg-harbor/10 rounded-md text-harbor">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-harbor">Email</h3>
                    <a href="mailto:contact@intermed-immobilier.com" className="text-muted-foreground hover:text-harbor transition-colors">
                      contact@intermed-immobilier.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <div className="p-3 bg-harbor/10 rounded-md text-harbor">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-harbor">Horaires</h3>
                    <p className="text-muted-foreground">
                      Du lundi au vendredi<br />
                      9h00 - 18h00
                    </p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="rounded-md overflow-hidden h-64 bg-gray-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2710.0!2d-1.556!3d47.22!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDEzJzEyLjAiTiAxwrAzMycyMS42Ilc!5e0!3m2!1sfr!2sfr!4v1600000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Localisation Inter'med Immobilier"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-lg" data-testid="contact-form-card">
                <CardContent className="p-8">
                  {submitted ? (
                    <div className="text-center py-12" data-testid="contact-success">
                      <div className="mx-auto p-4 bg-growth/10 rounded-full w-fit mb-6">
                        <CheckCircle2 className="text-growth" size={48} />
                      </div>
                      <h3 className="font-heading text-2xl text-harbor mb-4">
                        Message envoyé !
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        Merci pour votre message. Un conseiller Inter'med Immobilier
                        vous recontactera dans les plus brefs délais.
                      </p>
                      <Button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({
                            nom: "",
                            prenom: "",
                            telephone: "",
                            email: "",
                            message: "",
                            accepteContact: false,
                          });
                        }}
                        variant="outline"
                        className="border-harbor text-harbor hover:bg-harbor hover:text-white"
                      >
                        Envoyer un autre message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-heading text-xl text-harbor mb-6">
                        Envoyez-nous un message
                      </h3>
                      <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="nom">Nom *</Label>
                            <Input
                              id="nom"
                              value={formData.nom}
                              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                              className={errors.nom ? "border-red-500" : ""}
                              placeholder="Dupont"
                              data-testid="contact-input-nom"
                            />
                            {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
                          </div>
                          <div>
                            <Label htmlFor="prenom">Prénom *</Label>
                            <Input
                              id="prenom"
                              value={formData.prenom}
                              onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                              className={errors.prenom ? "border-red-500" : ""}
                              placeholder="Jean"
                              data-testid="contact-input-prenom"
                            />
                            {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="telephone">Téléphone *</Label>
                          <Input
                            id="telephone"
                            value={formData.telephone}
                            onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                            className={errors.telephone ? "border-red-500" : ""}
                            placeholder="06 12 34 56 78"
                            data-testid="contact-input-telephone"
                          />
                          {errors.telephone && <p className="text-red-500 text-xs mt-1">{errors.telephone}</p>}
                        </div>

                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className={errors.email ? "border-red-500" : ""}
                            placeholder="jean.dupont@email.com"
                            data-testid="contact-input-email"
                          />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <Label htmlFor="message">Message (optionnel)</Label>
                          <Textarea
                            id="message"
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            placeholder="Décrivez votre projet..."
                            rows={4}
                            data-testid="contact-input-message"
                          />
                        </div>

                        <div className="flex items-start gap-3 pt-2">
                          <Checkbox
                            id="accepteContact"
                            checked={formData.accepteContact}
                            onCheckedChange={(checked) => setFormData({ ...formData, accepteContact: checked })}
                            data-testid="contact-checkbox-accepte"
                          />
                          <Label htmlFor="accepteContact" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                            J'accepte d'être recontacté par Inter'med Immobilier pour discuter de mon projet. *
                          </Label>
                        </div>
                        {errors.accepteContact && <p className="text-red-500 text-xs">{errors.accepteContact}</p>}

                        <Button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-accent hover:bg-accent/90 text-white mt-4"
                          data-testid="contact-submit"
                        >
                          {loading ? "Envoi en cours..." : "Envoyer le message"}
                          <Send className="ml-2" size={18} />
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-12 bg-gray-50" data-testid="company-info">
        <div className="container-custom text-center">
          <p className="text-sm text-muted-foreground">
            <strong>Inter'med Immobilier</strong> - SIRET 942 124 348 00015<br />
            25 Rue de l'Indre, 44000 Nantes
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
